import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { environment } from 'src/environments/environment';
import { AsociacionPage } from 'src/app/domain/model/asociacion-empleado/asociacion-empleado';

/**
 * Adaptador HTTP genérico de los 5 catálogos de asociación catálogo↔empleado
 * (HU1259149). Endpoints: `/api/servicios/empleados/{id}/{recurso}`.
 *
 * <p>Deliberadamente NO sigue el patrón puerto abstracto + adaptador
 * concreto por dominio (domain/service + infrastructure/adapters, uno por
 * catálogo) que usa el resto del proyecto: los 5 recursos tienen la misma
 * forma HTTP exacta (listar/asociar-lote/modificar/desasociar), y Angular
 * DI resuelve por referencia de clase — una clase abstracta genérica
 * {@code AsociacionEmpleadoRepository<T>} sería el mismo token de DI para
 * los 5 catálogos y colisionaría al inyectar 5 instancias distintas (a
 * diferencia del backend Java, que sí puede tener 5 interfaces de gateway
 * separadas). El "puerto" real aquí es el parámetro {@code recurso}, no una
 * jerarquía de clases — ver Fase 3, HU1259149.md.</p>
 */
@Injectable({ providedIn: 'root' })
export class HttpAsociacionEmpleadoService {

  private readonly http = inject(HttpClient);

  private baseUrl(nmconsecutivoEmpleado: number, recurso: string): string {
    return `${environment.backend.baseUrl}/servicios/empleados/${nmconsecutivoEmpleado}/${recurso}`;
  }

  /**
   * @param filtros filtros opcionales del listado (hoy solo la cascada de
   * Zonas, hallazgo M-5). Los valores vacíos no se envían: el backend los
   * trata como "sin filtro".
   */
  async listar<T>(
    nmconsecutivoEmpleado: number,
    recurso: string,
    size: number,
    cursor?: string,
    direction?: 'NEXT' | 'PREVIOUS',
    filtros?: Record<string, string>
  ): Promise<AsociacionPage<T>> {
    let params = new HttpParams().set('size', size.toString());
    if (cursor?.trim()) params = params.set('cursor', cursor.trim());
    if (direction) params = params.set('direction', direction);
    params = this.agregarNoVacios(params, filtros);

    return firstValueFrom(
      this.http.get<AsociacionPage<T>>(this.baseUrl(nmconsecutivoEmpleado, recurso), { params })
    );
  }

  /**
   * Opciones de un nivel del panel de filtros en cascada
   * ({@code GET .../{recurso}/{subrecurso}}) — hallazgo M-5.
   */
  async listarOpcionesFiltro<T>(
    nmconsecutivoEmpleado: number,
    recurso: string,
    subrecurso: string,
    params?: Record<string, string>
  ): Promise<T[]> {
    const httpParams = this.agregarNoVacios(new HttpParams(), params);
    return firstValueFrom(
      this.http.get<T[]>(`${this.baseUrl(nmconsecutivoEmpleado, recurso)}/${subrecurso}`, { params: httpParams })
    );
  }

  private agregarNoVacios(params: HttpParams, valores?: Record<string, string>): HttpParams {
    for (const [clave, valor] of Object.entries(valores ?? {})) {
      if (valor !== null && valor !== undefined && `${valor}`.trim() !== '') {
        params = params.set(clave, `${valor}`.trim());
      }
    }
    return params;
  }

  async asociarLote<TResultado, TBody = unknown>(
    nmconsecutivoEmpleado: number,
    recurso: string,
    body: TBody
  ): Promise<TResultado> {
    return firstValueFrom(
      this.http.post<TResultado>(this.baseUrl(nmconsecutivoEmpleado, recurso), body)
    );
  }

  async modificar<TResultado, TBody = unknown>(
    nmconsecutivoEmpleado: number,
    recurso: string,
    nmconsecutivo: number,
    body: TBody
  ): Promise<TResultado> {
    return firstValueFrom(
      this.http.put<TResultado>(`${this.baseUrl(nmconsecutivoEmpleado, recurso)}/${nmconsecutivo}`, body)
    );
  }

  async desasociar<TResultado>(
    nmconsecutivoEmpleado: number,
    recurso: string,
    nmconsecutivo: number
  ): Promise<TResultado> {
    return firstValueFrom(
      this.http.put<TResultado>(`${this.baseUrl(nmconsecutivoEmpleado, recurso)}/${nmconsecutivo}/desasociar`, {})
    );
  }
}
